'use client';

import { useState } from 'react';
import { BundleCardCta } from '@components/templates/employer/job-bundles/cards/BundleCardCta';
import { BundleBenefits } from './BundleBenefits';

// Types
type HireTier = 'white' | 'blue';

interface HireBundleCardProps {
  hireBundleData?: ApiSchema<'OfferBundle'>;
  blueBundleData?: ApiSchema<'OfferBundle'>;
  iconColor?: string;
}

/**
 * @description Renders a combined Hire/Blue bundle card with tier selection tabs.
 * - "Hire" is the main title
 * - "White" tab shows the old Hire bundle content
 * - "Blue" tab shows the Blue bundle content
 */
export const HireBundleCard = (props: HireBundleCardProps) => {
  const { hireBundleData, blueBundleData, iconColor } = props;

  // Default to 'white' if hire bundle exists, otherwise 'blue'
  const defaultTier: HireTier = hireBundleData ? 'white' : 'blue';
  const [selectedTier, setSelectedTier] = useState<HireTier>(defaultTier);

  // Get the selected bundle based on tier (white = old hire, blue = blue bundle)
  const selectedBundle = selectedTier === 'white' ? hireBundleData : blueBundleData;

  // Fallback to available bundle if selected one doesn't exist
  const displayBundle = selectedBundle ?? hireBundleData ?? blueBundleData;

  // If neither bundle exists, render nothing
  if (!hireBundleData && !blueBundleData) {
    return null;
  }

  // Only show tier tabs when both bundles exist
  const showTierTabs = hireBundleData && blueBundleData;

  return (
    <div className="relative flex h-full flex-col rounded-md border bg-surface p-5">
      <div className="mb-3 min-h-16 leading-snug">
        {/* Bundle title with tier selection */}
        <div className="flex items-center justify-between gap-2">
          <h5 className="text-lg font-bold">
            Hire
          </h5>

          {/* Tier selection buttons - White/Blue (not translated) - only show when both exist */}
          {showTierTabs && (
            <div className="flex gap-1">
              {(['white', 'blue'] as HireTier[]).map((tier) => (
                <button
                  key={tier}
                  type="button"
                  onClick={() => setSelectedTier(tier)}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                    selectedTier === tier
                      ? 'bg-secondary text-white'
                      : 'bg-surface-100 text-ink hover:bg-surface-200'
                  }`}
                >
                  {tier === 'white' ? 'White' : 'Blue'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bundle subtitle - changes based on selected tier */}
        <p className="text-sm leading-tight text-ink-medium">
          {displayBundle?.subtitle}
        </p>
      </div>

      <div className="mt-3 flex-1">
        <BundleBenefits
          benefits={displayBundle?.benefits}
          iconColor={iconColor}
        />
      </div>

      <BundleCardCta bundleData={displayBundle} />
    </div>
  );
};
